import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip, Pagination, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem,
  InputAdornment,
} from '@mui/material';
import { Add, Search, Edit, Delete } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function ExpensesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [editCat, setEditCat] = useState(null); // category being edited
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { register: regCat, handleSubmit: submitCat, reset: resetCat } = useForm();

  const loadCats = () => api.get('/expenses/categories').then(r => setCategories(r.data.data));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/expenses', { params: { page, limit: 15, from: from || undefined, to: to || undefined } });
      setExpenses(data.data); setTotal(data.pagination.total);
    } finally { setLoading(false); }
  }, [page, from, to]);

  useEffect(() => { load(); loadCats(); }, [load]);

  const onSubmit = async (data) => {
    try {
      await api.post('/expenses', data);
      dispatch(showSnackbar({ message: 'Expense added', severity: 'success' }));
      setDialogOpen(false); reset(); load();
    } catch (e) {
      dispatch(showSnackbar({ message: 'Error', severity: 'error' }));
    }
  };

  const onAddCat = async (data) => {
    try {
      if (editCat) {
        await api.put(`/expenses/categories/${editCat.id}`, data);
        dispatch(showSnackbar({ message: 'Category updated', severity: 'success' }));
        setEditCat(null);
      } else {
        await api.post('/expenses/categories', data);
        dispatch(showSnackbar({ message: 'Category added', severity: 'success' }));
      }
      resetCat();
      loadCats();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const deleteCat = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await api.delete(`/expenses/categories/${cat.id}`);
      dispatch(showSnackbar({ message: 'Category deleted', severity: 'success' }));
      loadCats();
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    }
  };

  const totalAmount = expenses.reduce((s, e) => s + +e.amount, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.expenses')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setCatDialog(true)}>Manage Categories</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { reset(); setDialogOpen(true); }}>Add Expense</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" label="From" type="date" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="outlined" onClick={() => { setFrom(''); setTo(''); }}>Clear</Button>
            {totalAmount > 0 && (
              <Chip label={`Total: ₨ ${fmt(totalAmount)}`} color="error" sx={{ ml: 'auto' }} />
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {expenses.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{e.title}</Typography></TableCell>
                    <TableCell>{e.category_name || '—'}</TableCell>
                    <TableCell><Chip label={e.payment_method} size="small" /></TableCell>
                    <TableCell>{new Date(e.expense_date).toLocaleDateString('en-PK')}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700} color="error.main">₨ {fmt(e.amount)}</Typography></TableCell>
                    <TableCell>
                      <Button size="small" color="error" onClick={async () => {
                        if (!window.confirm('Delete expense?')) return;
                        await api.delete(`/expenses/${e.id}`);
                        dispatch(showSnackbar({ message: 'Deleted', severity: 'success' }));
                        load();
                      }}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={Math.ceil(total / 15)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Add Expense dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <Box component="form" id="exp-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}><TextField fullWidth label="Title *" {...register('title', { required: true })} error={!!errors.title} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Amount ₨ *" type="number" {...register('amount', { required: true, min: 0.01 })} error={!!errors.amount} /></Grid>
              <Grid item xs={6}>
                <TextField fullWidth select label="Category" defaultValue="" {...register('category_id')}>
                  <MenuItem value="">— Uncategorized —</MenuItem>
                  {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth type="date" label="Date *" InputLabelProps={{ shrink: true }} defaultValue={new Date().toISOString().split('T')[0]} {...register('expense_date', { required: true })} /></Grid>
              <Grid item xs={6}>
                <TextField fullWidth select label="Payment Method" defaultValue="cash" {...register('payment_method')}>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField fullWidth label="Notes" {...register('notes')} /></Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" type="submit" form="exp-form">{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Category management dialog */}
      <Dialog open={catDialog} onClose={() => { setCatDialog(false); setEditCat(null); resetCat(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Expense Categories</DialogTitle>
        <DialogContent>
          {/* Category list */}
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Category Name</TableCell>
                <TableCell>اردو</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell dir="rtl">{c.name_ur || '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditCat(c); resetCat({ name: c.name, name_ur: c.name_ur || '' }); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => deleteCat(c)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>No categories yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          {/* Add / Edit form */}
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {editCat ? `Editing: ${editCat.name}` : 'Add New Category'}
          </Typography>
          <Box component="form" id="cat-form" onSubmit={submitCat(onAddCat)} sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="Category Name *" {...regCat('name', { required: true })} sx={{ flex: 1 }} />
            <TextField size="small" label="اردو نام" {...regCat('name_ur')} inputProps={{ dir: 'rtl' }} sx={{ flex: 1 }} />
          </Box>
          {editCat && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => { setEditCat(null); resetCat(); }}>Cancel Edit</Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCatDialog(false); setEditCat(null); resetCat(); }}>Close</Button>
          <Button variant="contained" type="submit" form="cat-form" startIcon={editCat ? <Edit /> : <Add />}>
            {editCat ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
